import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const AUTH_BG = "https://static.prod-images.emergentagent.com/jobs/de8b7e50-5120-4fa2-90f1-4b615beda34b/images/9bc911f1bedab780b5b9f3e54a6247da2cde00442cb80da5d0fbac04a8e9a31e.png";

export default function Register() {
  const { t, lang, setLang } = useI18n();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", company_name: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await register({ ...form, language: lang });
      toast.success(t("success"));
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white" data-testid="register-page">
      <div className="flex-1 flex flex-col px-6 sm:px-12 md:px-20 py-10 max-w-2xl mx-auto md:mx-0">
        <header className="flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <div className="font-heading text-2xl font-bold tracking-tighter text-zinc-950">Cornerstone</div>
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-cs-accent" />
          </div>
          <select value={lang} onChange={(e) => setLang(e.target.value)} className="text-xs bg-transparent border-0 text-zinc-600" data-testid="register-language-select">
            <option value="pt">PT</option><option value="en">EN</option>
          </select>
        </header>

        <div className="flex-1 flex flex-col justify-center max-w-md mx-auto w-full">
          <div className="cs-label">{t("by_company")}</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-zinc-950 mt-3" data-testid="register-title">{t("register_title")}</h1>
          <p className="text-sm text-zinc-600 mt-2 text-balance">{t("register_subtitle")}</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="register-form">
            {[
              { key: "name", type: "text", placeholder: "João Silva", autoComplete: "name" },
              { key: "company_name", type: "text", placeholder: "Construtora ABC", autoComplete: "organization" },
              { key: "email", type: "email", placeholder: "voce@empresa.com.br", autoComplete: "email" },
              { key: "password", type: "password", placeholder: "••••••••", autoComplete: "new-password" },
            ].map((f) => (
              <div key={f.key}>
                <label className="cs-label block mb-1.5">{t(f.key)}</label>
                <input
                  type={f.type} required value={form[f.key]} autoComplete={f.autoComplete}
                  onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-zinc-300 rounded-md bg-white text-sm font-mono text-zinc-900 focus:outline-none focus:ring-2 focus:ring-cs-accent focus:border-cs-accent transition-colors"
                  placeholder={f.placeholder}
                  data-testid={`register-${f.key}-input`}
                />
              </div>
            ))}

            {error && <div className="text-sm text-red-600 font-medium border-l-2 border-red-600 pl-3 py-1" data-testid="register-error">{error}</div>}

            <button type="submit" disabled={submitting}
              className="w-full bg-zinc-950 text-white py-2.5 rounded-md text-sm font-semibold tracking-wide hover:bg-zinc-800 transition-colors disabled:opacity-60"
              data-testid="register-submit-btn"
            >
              {submitting ? "..." : t("register_submit")}
            </button>
          </form>

          <p className="mt-8 text-sm text-zinc-600">
            {t("have_account")} <Link to="/login" className="text-cs-accent font-semibold hover:underline" data-testid="login-link">{t("sign_in")}</Link>
          </p>
        </div>
      </div>

      <div className="hidden md:block flex-1 relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${AUTH_BG}')` }} />
        <div className="absolute inset-0 bg-zinc-950/30" />
      </div>
    </div>
  );
}
